import { Injectable, NgZone } from '@angular/core';

@Injectable({providedIn: 'root'})
export class ImageSrcService {
  private observers = new Map<HTMLElement, MutationObserver>();
  private nearViewportCallbacks = new Map<Element, () => void>();
  private nearViewportObserver: IntersectionObserver | null = null;

  constructor(private ngZone: NgZone) {
  }

  observeVisibilityChanges(
    element: HTMLElement,
    callback: (rect: DOMRect) => void,
    delay = 10,
  ) {
    if (!element) return;

    // Kontener już widoczny i zmierzony - ustaw src od razu (mikrozadanie, żeby nie zmieniać
    // bindingu w trakcie sprawdzania zmian), zamiast czekać na setTimeout + rAF.
    if (this.isVisible(element)) {
      const rect = element.getBoundingClientRect();
      if (rect.width > 0) {
        queueMicrotask(() => this.ngZone.run(() => callback(rect)));
        return;
      }
    }

    // Zarejestruj później
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        requestAnimationFrame(() => {
          const observer = new MutationObserver(() => {
            if (this.isVisible(element)) {
              const rect = element.getBoundingClientRect();
              this.ngZone.run(() => {
                callback(rect);
              });
              this.disconnectObserver(element);
            }
          });

          let el: HTMLElement | null = element;
          while (el) {
            observer.observe(el, {
              attributes: true,
              attributeFilter: ['hidden', 'style'],
            });
            el = el.parentElement;
          }

          this.observers.set(element, observer);

          // na wypadek gdyby był już widoczny
          if (this.isVisible(element)) {
            const rect = element.getBoundingClientRect();
            callback(rect);
            this.disconnectObserver(element);
          }
        });
      }, delay);
    });
  }

  /**
   * Wywołuje callback jednorazowo, gdy element zbliży się do widocznego obszaru na `rootMargin`
   * (domyślnie ~2,5 ekranu w pionie). Pozwala zacząć pobieranie obrazów z wyprzedzeniem -
   * natywny `loading="lazy"` w Safari/iOS startuje dopiero tuż przed wjazdem na ekran.
   * Jeden współdzielony observer na wszystkie obrazy.
   */
  observeNearViewport(element: Element, callback: () => void, rootMargin = '250% 0px') {
    if (!element) return;

    if (typeof IntersectionObserver === 'undefined') {
      callback();
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      if (!this.nearViewportObserver) {
        this.nearViewportObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const cb = this.nearViewportCallbacks.get(entry.target);
            this.unobserveNearViewport(entry.target);
            if (cb) {
              this.ngZone.run(cb);
            }
          }
        }, {rootMargin});
      }
      this.nearViewportCallbacks.set(element, callback);
      this.nearViewportObserver.observe(element);
    });
  }

  unobserveNearViewport(element: Element) {
    this.nearViewportCallbacks.delete(element);
    this.nearViewportObserver?.unobserve(element);
  }

  private isVisible(el: HTMLElement): boolean {
    while (el) {
      if (el.hasAttribute('hidden') || getComputedStyle(el).display === 'none') {
        return false;
      }
      el = el.parentElement;
    }
    return true;
  }

  disconnectObserver(element: HTMLElement) {
    const observer = this.observers.get(element);
    if (observer) {
      observer.disconnect();
      this.observers.delete(element);
    }
  }
}
